import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { LoadingState } from '../ui/loading-state';
import { Breadcrumbs } from '../ui/breadcrumbs';
import {
  useTicketCategoryManagers,
  useCreateTicketCategoryManager,
  useUpdateTicketCategoryManager,
  useDeleteTicketCategoryManager
} from '../../lib/supabase/tickets/supabaseTicketCategoryManagerService';
import { useUsers } from '../../lib/supabase/users/supabaseUserService';
import { useRoles } from '../../lib/supabase/roles/supabaseRoleService';
import { TicketCategoryManager, TicketType } from '@shiftly/shared/src/schema';
import { Permissions } from '@shiftly/shared/src/permissions';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'react-hot-toast';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getUserInfo, getTicketTypeName } from '../../lib/utils';
import { Check, ChevronsUpDown, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { DataTable } from '../DataTable';
import { ColumnDef } from '@tanstack/react-table';

const TicketCategoryManagersView = () => {
  const { permissions, loading } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedManager, setSelectedManager] = useState<TicketCategoryManager | null>(null);
  const [formData, setFormData] = useState<{
    ticket_type: TicketType;
    pay_class: 'UK' | 'FP';
    manager_ids: string[];
    role_ids: string[];
  }>({
    ticket_type: 'misc',
    pay_class: 'UK',
    manager_ids: [],
    role_ids: []
  });
  const [commandOpen, setCommandOpen] = useState(false);
  const [rolesCommandOpen, setRolesCommandOpen] = useState(false);

  // Fetch category managers
  const { data: categoryManagers = [], isLoading: categoryManagersLoading } = useTicketCategoryManagers();
  
  // Fetch users for the dropdown
  const { data: users = [], isLoading: usersLoading } = useUsers();
  
  // Fetch roles
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  
  // Mutations
  const createCategoryManager = useCreateTicketCategoryManager();
  const updateCategoryManager = useUpdateTicketCategoryManager();
  const deleteCategoryManager = useDeleteTicketCategoryManager();
  
  // Check if user has permission to manage category managers
  const canManageCategoryManagers = permissions.includes(Permissions.TICKETS_CATEGORY_MANAGERS);
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      setIsEditMode(false);
      setSelectedManager(null);
      setFormData({
        ticket_type: 'misc',
        pay_class: 'UK',
        manager_ids: [],
        role_ids: []
      });
    }
  }, [isDialogOpen]);
  
  // Move these functions up before columns definition since they're used there
  const handleOpenEditDialog = (manager: TicketCategoryManager) => {
    setIsEditMode(true);
    setSelectedManager(manager);
    setFormData({
      ticket_type: manager.ticket_type,
      pay_class: manager.pay_class,
      manager_ids: manager.manager_ids,
      role_ids: manager.role_ids
    });
    setIsDialogOpen(true);
  };
  
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category manager?')) {
      try {
        await deleteCategoryManager.mutateAsync(id);
        toast.success('Category manager deleted successfully');
      } catch (error) {
        console.error('Error deleting category manager:', error);
        toast.error('Failed to delete category manager');
      }
    }
  };
  
  // Define columns for DataTable - moved up before conditional returns
  const columns = useMemo<ColumnDef<TicketCategoryManager>[]>(() => [
    {
      accessorKey: 'ticketType',
      header: 'Ticket Type',
      cell: ({ row }) => <span className="font-medium">{getTicketTypeName(row.original.ticket_type)}</span>
    },
    {
      accessorKey: 'pay_class',
      header: 'Pay Class',
      cell: ({ row }) => <span>{row.original.pay_class}</span>
    },
    {
      accessorKey: 'managerIds',
      header: 'Managers & Roles',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.manager_ids.map(managerId => {
            const userInfo = getUserInfo(managerId, users);
            return (
              <div key={managerId} className="flex items-center gap-1.5 bg-brand-gold dark:bg-brand-dark px-2 py-1 rounded-full">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-[10px]">{userInfo.initials}</AvatarFallback>
                </Avatar>
                <span className="text-xs">{userInfo.full_name}</span>
              </div>
            );
          })}
          {row.original.role_ids.map(roleId => {
            const role = roles.find(r => r.id === roleId);
            return role ? (
              <div key={roleId} className="flex items-center gap-1.5 bg-brand-green dark:bg-brand-dark px-2 py-1 rounded-full">
                <span className="text-xs font-medium capitalize">{role.name}</span>
              </div>
            ) : null;
          })}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex -space-x-1 justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            description="Edit category"
            onClick={(e) => {
                e.stopPropagation();
                handleOpenEditDialog(row.original);
            }}
            >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            description="Delete category"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.original.id);
            }}
          >
            <Trash2 className="h-4 w-4 text-brand-red" />
          </Button>
        </div>
      )
    }
  ], [users, roles, deleteCategoryManager]);
  
  if (loading || categoryManagersLoading || usersLoading || rolesLoading) {
    return <LoadingState />;
  }
  
  // If user doesn't have permission, show access denied
  if (!canManageCategoryManagers) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
            Access Denied
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground">
            You don't have permission to manage ticket category managers.
          </p>
        </div>
      </div>
    );
  }
  
  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Tickets', href: '/tickets' },
    { title: 'Category Managers' }
  ];
  
  const handleOpenCreateDialog = () => {
    setIsEditMode(false);
    setSelectedManager(null);
    setFormData({
      ticket_type: 'misc',
      pay_class: 'UK',
      manager_ids: [],
      role_ids: []
    });
    setIsDialogOpen(true);
  };
  
  const handleSave = async () => {
    try {
      // Check for duplicate ticket type and pay class combination
      const isDuplicate = categoryManagers.some(manager => 
        manager.ticket_type === formData.ticket_type && 
        manager.pay_class === formData.pay_class && 
        (!isEditMode || manager.id !== selectedManager?.id)
      );

      if (isDuplicate) {
        toast.error('A category manager with this ticket type and pay class already exists');
        return;
      }

      if (isEditMode && selectedManager) {
        await updateCategoryManager.mutateAsync({
          id: selectedManager.id,
          data: formData
        });
        toast.success('Category manager updated successfully');
      } else {
        await createCategoryManager.mutateAsync(formData);
        toast.success('Category manager created successfully');
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving category manager:', error);
      toast.error('Failed to save category manager');
    }
  };
  
  // Toggle user selection
  const toggleUser = (user_id: string) => {
    setFormData(current => {
      const currentManagerIds = [...current.manager_ids];
      if (currentManagerIds.includes(user_id)) {
        return {
          ...current,
          manager_ids: currentManagerIds.filter(id => id !== user_id)
        };
      } else {
        return {
          ...current,
          manager_ids: [...currentManagerIds, user_id]
        };
      }
    });
  };

  // Remove a user from selection
  const removeUser = (user_id: string, e: React.MouseEvent) => {
    e.preventDefault();
    setFormData(current => ({
      ...current,
      manager_ids: current.manager_ids.filter(id => id !== user_id)
    }));
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
              Ticket Category Managers
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground">
              Manage default participants for ticket categories
            </p>
          </div>
          <Button onClick={handleOpenCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            New Category Manager
          </Button>
        </div>
      </div>
      
      {categoryManagers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-6">
            <p className="text-muted-foreground dark:text-muted-foreground">No category managers found</p>
            <Button onClick={handleOpenCreateDialog} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Create Category Manager
            </Button>
          </CardContent>
        </Card>
      ) : (
        <DataTable 
          data={categoryManagers} 
          columns={columns}
          searchPlaceholder="Search category managers..."
          tableId="ticket-category-managers"
          initialSorting={[{ id: 'pay_class', desc: true }]}
        />
      )}
      
      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Category Manager' : 'Create Category Manager'}
            </DialogTitle>
            <DialogDescription>
              {isEditMode 
                ? 'Update the category manager details below.' 
                : 'Configure which users should be automatically added to tickets of a specific type and pay class.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ticketType">Ticket Type</Label>
              <Select
                value={formData.ticket_type}
                onValueChange={(value) => setFormData({ ...formData, ticket_type: value as TicketType })}
              >
                <SelectTrigger id="ticketType">
                  <SelectValue placeholder="Select ticket type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="misc">Misc</SelectItem>
                  <SelectItem value="payment">Payment/Invoicing</SelectItem>
                  <SelectItem value="staffing">Account/Shift Change</SelectItem>
                  <SelectItem value="complaints">Complaints</SelectItem>
                  <SelectItem value="timeOff">Time Off</SelectItem>
                  <SelectItem value="timeOffUrgent">Time Off (Urgent)</SelectItem>
                  <SelectItem value="streamTimes">Stream Times</SelectItem>
                  <SelectItem value="accountAccess">Account Access</SelectItem>
                  <SelectItem value="overwatch">Overwatch</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="scheduling">Scheduling</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay_class">Pay Class</Label>
              <Select
                value={formData.pay_class}
                onValueChange={(value) => setFormData({ ...formData, pay_class: value as 'UK' | 'FP' })}
              >
                <SelectTrigger id="pay_class">
                  <SelectValue placeholder="Select pay class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">UK</SelectItem>
                  <SelectItem value="FP">FP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="roles">Roles</Label>
              <Popover open={rolesCommandOpen} onOpenChange={setRolesCommandOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={rolesCommandOpen}
                    className="w-full justify-between bg-white dark:bg-brand-dark text-brand-dark dark:text-foreground border-brand-border dark:border-brand-border"
                  >
                    <span className="truncate">
                      {formData.role_ids.length === 0
                        ? "Select roles..."
                        : `${formData.role_ids.length} role${formData.role_ids.length === 1 ? "" : "s"} selected`}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search roles..." />
                    <CommandList>
                      <CommandEmpty>No roles found.</CommandEmpty>
                      <CommandGroup>
                        {roles.map((role) => (
                          <CommandItem
                            key={role.id}
                            onSelect={() => {
                              setFormData(current => {
                                const currentRoleIds = [...current.role_ids];
                                if (currentRoleIds.includes(role.id)) {
                                  return {
                                    ...current,
                                    role_ids: currentRoleIds.filter(id => id !== role.id)
                                  };
                                } else {
                                  return {
                                    ...current,
                                    role_ids: [...currentRoleIds, role.id]
                                  };
                                }
                              });
                            }}
                          >
                            <div className="flex items-center">
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.role_ids.includes(role.id) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="capitalize">{role.name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formData.role_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-1">
                  {formData.role_ids.map((role_id) => {
                    const role = roles.find(r => r.id === role_id);
                    if (!role) return null;
                    return (
                      <button
                        key={role_id}
                        className="flex items-center gap-2 bg-brand-dark hover:bg-brand-dark dark:bg-brand-dark dark:hover:bg-brand-dark px-3 py-1.5 rounded-md text-sm transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          setFormData(current => ({
                            ...current,
                            role_ids: current.role_ids.filter(id => id !== role_id)
                          }));
                        }}
                      >
                        {role.name}
                        <X className="h-3.5 w-3.5" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="managers">Individual Managers</Label>
              <Popover open={commandOpen} onOpenChange={setCommandOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={commandOpen}
                    className="w-full justify-between bg-white dark:bg-brand-dark text-brand-dark dark:text-foreground border-brand-border dark:border-brand-border"
                  >
                    <span className="truncate">
                      {formData.manager_ids.length === 0
                        ? "Select managers..."
                        : `${formData.manager_ids.length} manager${formData.manager_ids.length === 1 ? "" : "s"} selected`}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandList>
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup>
                        {users.map((user) => (
                          <CommandItem
                            key={user.id}
                            onSelect={() => toggleUser(user.id)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.manager_ids.includes(user.id) ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {user.full_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formData.manager_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-1">
                  {formData.manager_ids.map((user_id) => {
                    const user = users.find(u => u.id === user_id);
                    if (!user) return null;
                    return (
                      <button
                        key={user_id}
                        className="flex items-center gap-2 bg-brand-dark hover:bg-brand-dark dark:bg-brand-dark dark:hover:bg-brand-dark px-3 py-1.5 rounded-md text-sm transition-colors"
                        onClick={(e) => removeUser(user_id, e)}
                      >
                        {user.full_name}
                        <X className="h-3.5 w-3.5" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketCategoryManagersView; 